<template>
    <div>
        <div
            class="flex items-center h-11"
            role="group"
            aria-labelledby="sliderHeading">
            <VueSlider
                v-if="parents.length > 0"
                :contained="true"
                :dot-options="dotOptions"
                :enable-cross="false"
                :min-range="1"
                :process="process"
                :tooltip="'none'"
                width="100%"
                v-model="value">
            </VueSlider>
        </div>
        <div class="flex flex-wrap items-center justify-center mt-4">
            <div
                v-for="(parent, i) in parents"
                class="flex items-center mr-6 mb-6">
                <div class="rounded-l leading-8 px-2" :class="colors.labels[i]">
                    {{ parent.name }}
                </div>
                <div
                    class="font-mono text-sm leading-8 text-center rounded-r px-2 w-12"
                    :class="colors.values[i]">
                    {{ Math.round(ranges[i]) }}%
                </div>
                <!-- Warning indicator for insufficient questions -->
                <div
                    v-if="!hasEnoughQuestions(i)"
                    class="ml-2 text-red-500 text-sm"
                >
                    ⚠️
                </div>
            </div>
        </div>

        <!-- Error messages for invalid configurations -->
        <div v-if="hasValidationErrors" class="mt-4">
            <div class="bg-red-50 border border-red-200 rounded-md p-4">
                <div class="flex">
                    <div class="flex-shrink-0">
                        ⚠️
                    </div>
                    <div class="ml-3">
                        <h3 class="text-sm font-medium text-red-800">
                            Cannot create exam - insufficient questions
                        </h3>
                        <div class="mt-2 text-sm text-red-700">
                            <ul class="list-disc pl-5 space-y-1">
                                <li v-for="error in validationErrors" :key="error.category">
                                    <strong>{{ error.category }}:</strong>
                                    Need {{ error.required }} questions, only {{ error.available }} available
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
// @ts-nocheck
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";

export default {
    components: {
        VueSlider,
    },
    props: {
        parents: Array,
        examLength: {
            type: Number,
            default: 25
        },
        counts: {
            type: Object,
            default: () => ({})
        },
        selectedCategories: {
            type: Array,
            default: () => []
        },
        categories: {
            type: Array,
            default: () => []
        }
    },
    data(): any {
        return {
            value: [],
            lastValidValue: [],
        };
    },
    computed: {
        breakdown() {
            let breakdown = [];
            for (let i = 0; i < this.parents.length; i++) {
                breakdown.push((this.weights[i] / this.total) * 100);
            }
            return breakdown;
        },
        colors() {
            return {
                labels: [
                    "bg-indigo-50 text-indigo-800",
                    "bg-cyan-50 text-cyan-800",
                    "bg-pink-50 text-pink-800",
                    "bg-orange-50 text-orange-800",
                ],
                rails: ["#4338ca", "#0e7490", "#be185d", "#c2410c"],
                values: [
                    "bg-indigo-700 text-white",
                    "bg-cyan-700 text-white",
                    "bg-pink-700 text-white",
                    "bg-orange-700 text-white",
                ],
            };
        },
        dotOptions() {
            let dots = [];
            for (let i = 0; i <= this.parents.length; i++) {
                if (i == 0 || i == this.parents.length) {
                    dots.push({ disabled: true });
                } else {
                    dots.push({ disabled: false });
                }
            }
            return dots;
        },
        process() {
            return (dots) => {
                let process = [];
                for (let i = 0; i < this.parents.length; i++) {
                    process.push([
                        dots[i],
                        dots[i + 1],
                        { backgroundColor: this.colors.rails[i] },
                    ]);
                }
                return process;
            };
        },
        ranges() {
            let ranges = [];
            for (let i = 0; i < this.parents.length; i++) {
                ranges.push(this.value[i + 1] - this.value[i]);
            }
            return ranges;
        },
        total() {
            return this.weights.reduce((a, b) => a + b);
        },
        values() {
            let values = [0];
            for (let i = 0; i < this.parents.length; i++) {
                values.push(values[values.length - 1] + this.breakdown[i]);
            }
            return values;
        },
        weights() {
            return this.parents.map((o) => o.weight);
        },

        hasValidationErrors() {
            return this.validationErrors.length > 0;
        },

        validationErrors() {
            const errors = [];

            for (let i = 0; i < this.parents.length; i++) {
                const required = this.getRequiredQuestions(i);
                const available = this.getAvailableQuestions(i);

                if (required > available) {
                    errors.push({
                        category: this.parents[i].name,
                        required: required,
                        available: available
                    });
                }
            }

            return errors;
        },
    },
    methods: {
        resetValues() {
            this.value = this.values;
            this.lastValidValue = [...this.values];
        },

        getRequiredQuestions(categoryIndex) {
            const percentage = this.ranges[categoryIndex];
            return Math.ceil((percentage / 100) * this.examLength);
        },

        getAvailableQuestions(categoryIndex) {
            if (!this.parents[categoryIndex]) {
                return 0;
            }

            const parentName = this.parents[categoryIndex].name;

            const parentCategory = this.categories.find(cat => cat.name === parentName);
            if (!parentCategory) {
                return 0;
            }

            // If the parent is selected, return its count (which includes children)
            if (this.isCategorySelected(parentCategory)) {
                return this.counts[parentCategory.uuid] || 0;
            }

            // If parent not selected, sum only the selected children
            return this.getCountFromSelectedChildren(parentCategory.children || []);
        },

        getCountFromSelectedChildren(children) {
            let totalCount = 0;

            children.forEach(child => {
                if (this.isCategorySelected(child)) {
                    totalCount += this.counts[child.uuid] || 0;
                } else if (child.children && child.children.length > 0) {
                    // If child not selected, check its children (handle topics)
                    totalCount += this.getCountFromSelectedChildren(child.children);
                }
            });

            return totalCount;
        },

        isCategorySelected(category) {
            return this.selectedCategories.some(selected => selected.uuid === category.uuid);
        },

        hasEnoughQuestions(categoryIndex) {
            const required = this.getRequiredQuestions(categoryIndex);
            const available = this.getAvailableQuestions(categoryIndex);
            return available >= required;
        },
    },
    mounted() {
        this.$events.$on("resetCategoryBreakdown", () => {
            this.resetValues();
        });

        // Initialize lastValidValue
        this.lastValidValue = [...this.value];
    },
    watch: {
        ranges(value) {
            this.$emit("updateBreakdown", value);
        },
        value(value) {
            if (value != this.values) {
                this.$events.$emit("showCategoryReset");
            } else {
                this.$events.$emit("hideCategoryReset");
            }
        },
        values(newValue, oldValue) {
            if (JSON.stringify(newValue) != JSON.stringify(oldValue)) {
                this.value = newValue;
                this.lastValidValue = [...newValue];
            }
        },
        hasValidationErrors(hasErrors) {
            this.$emit("validationChanged", !hasErrors);
        }
    },
};
</script>

<style>
.vue-slider-dot-handle-disabled {
    background-color: white !important;
}

.vue-slider-dot-handle {
    border: 1px solid gray;
}

.vue-slider-dot-handle-focus {
    @apply border-2 border-blue-700;
}
</style>
